import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  private trackingId: string | null = null;
  private siteVerification: string | null = null;
  private isInitialized = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  initialize(trackingId: string, siteVerification?: string): void {
    if (!isPlatformBrowser(this.platformId) || !trackingId || this.isInitialized) {
      return;
    }

    this.trackingId = trackingId;
    this.siteVerification = siteVerification || null;
    this.isInitialized = true;

    // Initialize data layer first
    window.dataLayer = window.dataLayer || [];
    
    // Define gtag function
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Add site verification meta tag if provided
    this.addSiteVerification();

    // Load the Google Analytics script
    this.loadAnalyticsScript();
  }

  private addSiteVerification(): void {
    if (!this.siteVerification) return;

    // Remove existing verification meta tag if it exists
    const existingMeta = document.querySelector('meta[name="google-site-verification"]');
    if (existingMeta) {
      existingMeta.remove();
    }

    // Add the verification meta tag
    const meta = document.createElement('meta');
    meta.name = 'google-site-verification';
    meta.content = this.siteVerification;
    document.head.appendChild(meta);
  }

  private loadAnalyticsScript(): void {
    if (!this.trackingId) return;

    // Load the main Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`;
    
    // Create the inline script element
    const inlineScript = document.createElement('script');
    inlineScript.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${this.trackingId}');
    `;
    
    // When the external script loads, append the inline script
    script.onload = () => {
      document.head.appendChild(inlineScript);
      console.log('Google Analytics initialized with ID:', this.trackingId);
    };
    
    // Handle script loading errors
    script.onerror = (error) => {
      console.error('Failed to load Google Analytics script:', error);
    };
    
    // Append the external script first
    document.head.appendChild(script);
  }
}
